import { useState } from 'react';
import {
  Table, Button, Space, Tag, Select, message, Popconfirm, Badge, Card, Statistic, Row, Col
} from 'antd';
import { DeleteOutlined, SyncOutlined, CloudServerOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../api/agents';
import { firmsApi } from '../api/firms';
import type { Agent, Firm } from '../types';

export function Agents() {
  const [selectedFirmId, setSelectedFirmId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: firms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: firmsApi.getAll,
  });

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['agents', selectedFirmId],
    queryFn: () => selectedFirmId ? agentsApi.getByFirm(selectedFirmId) : Promise.resolve([]),
    enabled: !!selectedFirmId,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ firmId, id }: { firmId: number; id: number }) =>
      agentsApi.delete(firmId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      message.success('Agent silindi');
    },
    onError: () => message.error('Agent silinemedi'),
  });

  const onlineAgents = agents.filter(a => a.isOnline).length;
  const totalAgents = agents.length;

  const columns = [
    {
      title: 'Agent Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Agent) => (
        <Space>
          <Badge status={record.isOnline ? 'success' : 'default'} />
          {name}
        </Space>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_: unknown, record: Agent) => (
        <Tag color={record.isOnline ? 'green' : 'default'}>
          {record.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
        </Tag>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (apiKey: string) => (
        <code style={{ fontSize: '11px' }}>{apiKey.substring(0, 20)}...</code>
      ),
    },
    {
      title: 'Son Görülme',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('tr-TR') : '-',
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: Agent) => (
        <Space>
          <Popconfirm
            title="Agent'ı silmek istediğinize emin misiniz?"
            description="Bu işlem agent'ı deaktif eder."
            onConfirm={() => selectedFirmId && deleteMutation.mutate({
              firmId: selectedFirmId,
              id: record.id
            })}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Agentlar</h2>
        <Select
          style={{ width: 300 }}
          placeholder="Firma seçin"
          value={selectedFirmId}
          onChange={setSelectedFirmId}
          options={firms.map((f: Firm) => ({ value: f.id, label: f.name }))}
          allowClear
        />
      </div>

      {selectedFirmId && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Toplam Agent"
                  value={totalAgents}
                  prefix={<CloudServerOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Çevrimiçi"
                  value={onlineAgents}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<SyncOutlined spin={onlineAgents > 0} />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Çevrimdışı"
                  value={totalAgents - onlineAgents}
                  valueStyle={{ color: totalAgents - onlineAgents > 0 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={agents}
            rowKey="id"
            loading={isLoading}
            locale={{ emptyText: 'Bu firmaya ait agent bulunamadı' }}
          />
        </>
      )}

      {!selectedFirmId && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CloudServerOutlined style={{ fontSize: 48, color: '#999', marginBottom: 16 }} />
            <p style={{ color: '#666' }}>
              Agent listesini görmek için yukarıdan bir firma seçin.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
